/**
 * Pure async utilities for the three streaming-recovery strategies.
 *
 * useStreamingPoller and the dashboard drawer orchestrate these; they do not
 * implement them.
 *
 *  Stage 1 – fetchAndApplyBackendState
 *    Fetches accumulated state from Redis, applies it to the stream
 *    processor, and resolves terminal states (complete / error / not_found).
 *
 *  Stage 2 – SSE reconnect (reconnectWithState)
 *    Connects to the SSE stream starting from the lastEventId returned
 *    by Stage 1 and consumes events until the stream closes.
 *
 *  resumeUntilTerminal repeats Stages 1 and 2 until Stage 1 resolves.
 *
 *  Stage 3 – startRestPolling
 *    Reads the final row from the REST messages endpoint once Redis no
 *    longer holds the message (not_found).
 */

import type { MessageStateResponse } from '@/interfaces/thread.interface';
import type { ThreadMessage } from '@/types/messages';
import type { LLMEventStreamProcessor } from '@/services/LLMEventStreamProcessor';
import { reconnectWithState } from '@/utils/streamReconnect';
import { getMessageState, getThreadMessages } from '@/api/threads.api';
import { transformApiMessagesToThreadMessages } from '../messages/MessageTransformer';
import { logger } from '@/utils/logger';
import {
  PAINT_DELAY_MS,
  POLL_MAX_ATTEMPTS,
  RECONNECT_BACKOFF_MAX_MS,
  RECONNECT_BACKOFF_MS,
  STREAM_START_ID,
} from '@/constants/ui.constants';

export type SetMessages = (
  updater:
    | ThreadMessage[]
    | ((prev: ThreadMessage[]) => ThreadMessage[])
) => void;

/** How the backend says a message ended. */
export type TerminalStatus = 'complete' | 'error' | 'not_found';

export type BackendStateFetchResult =
  | { resolved: true; terminalStatus: TerminalStatus }
  | { resolved: false; lastEventId: string };

function isTerminalStatus(status: string): status is 'complete' | 'error' {
  return status === 'complete' || status === 'error';
}

function applyTerminalStatus(
  setMessages: SetMessages,
  messageId: string,
  status: 'complete' | 'error',
  content?: MessageStateResponse['content'],
  metadata?: MessageStateResponse['metadata'],
): void {
  setMessages((prev) =>
    prev.map((msg) => {
      if (msg.id !== messageId) return msg;
      const patch: Partial<ThreadMessage> = { status };
      if (content !== undefined) patch.content = content;
      // Merge backend-aggregated metadata (subgraph, availableIds,
      // tokenUsage, …) into the local message.  Without this, a
      // Reconnect that finds the backend in a terminal state would
      // strip the artefacts the user can otherwise see after a hard
      // refresh (which loads metadata directly from Postgres).
      if (metadata) {
        patch.metadata = { ...msg.metadata, ...metadata };
      }
      return { ...msg, ...patch };
    })
  );
}

/**
 * Stage 1: Fetches the current accumulated state from the backend Redis
 * store and applies it to the stream processor.
 *
 * Returns `{ resolved: true }` when the message has ended: complete or
 * error (applied to the message), or not_found — Redis no longer holds it
 * and Postgres has its final row.
 * Returns `{ resolved: false, lastEventId }` when the message is still
 * in progress and the caller should proceed with SSE reconnect.
 *
 * @throws when the state cannot be fetched.
 */
export async function fetchAndApplyBackendState(
  projectId: string,
  threadId: string,
  messageId: string,
  streamProcessor: LLMEventStreamProcessor,
  setMessages: SetMessages,
): Promise<BackendStateFetchResult> {
  const state = await getMessageState(projectId, threadId, messageId);

  if (state.status === 'not_found') {
    return { resolved: true, terminalStatus: 'not_found' };
  }

  const hasContent = state.content.length > 0;
  if (hasContent) {
    const contentTypes = state.content.map(
      (b: { type: string }) => b.type,
    );
    const textBlockCount = state.content.filter(
      (b: { type: string }) => b.type === 'text',
    ).length;

    logger.info(
      `[BackendState] restoring ${state.content.length} blocks ` +
        `(types=[${contentTypes.join(',')}], ` +
        `textBlocks=${textBlockCount}) ` +
        `lastEventId=${state.lastEventId} ` +
        `eventOrder=${state.eventOrder} ` +
        `to message ${messageId}`,
    );

    streamProcessor.applyBackendState(
      messageId,
      state.content,
      state.eventOrder,
      state.metadata,
    );
  }

  if (isTerminalStatus(state.status)) {
    applyTerminalStatus(
      setMessages,
      messageId,
      state.status,
      state.status === 'complete' && hasContent ? state.content : undefined,
      state.metadata,
    );
    logger.info(
      `[BackendState] Message ${messageId} is ${state.status} in Redis`,
    );
    return { resolved: true, terminalStatus: state.status };
  }

  return {
    resolved: false,
    lastEventId: hasContent ? state.lastEventId : STREAM_START_ID,
  };
}

/** Resolves after `ms`, or at once when `signal` aborts. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done);
  });
}

/**
 * Stages 1 and 2 until Stage 1 resolves. A stream that closes without ending
 * the message (clean close, network error, stream_timeout frame) is resumed
 * after a capped backoff: an ask_user call can hold a turn open for hours.
 *
 * Resolves with the terminal status, or `aborted` once `controller` aborts.
 */
export async function resumeUntilTerminal(
  projectId: string,
  threadId: string,
  messageId: string,
  getAccessToken: () => string,
  controller: AbortController,
  streamProcessor: LLMEventStreamProcessor,
  setMessages: SetMessages,
): Promise<TerminalStatus | 'aborted'> {
  const { signal } = controller;
  let failures = 0;
  let lastEventId: string | null = null;
  for (let attempt = 0; !signal.aborted; attempt += 1) {
    // Straight back to /state after a terminal event, else back off.
    if (attempt > 0 && !streamProcessor.hasReceivedFinished(threadId)) {
      const backoff = RECONNECT_BACKOFF_MS * 2 ** failures;
      await pause(Math.min(backoff, RECONNECT_BACKOFF_MAX_MS), signal);
      if (signal.aborted) break;
    }
    // Each connection parses from a clean slate; Stage 1 reseeds the rest.
    streamProcessor.startNewMessage(threadId);
    try {
      const state = await fetchAndApplyBackendState(
        projectId,
        threadId,
        messageId,
        streamProcessor,
        setMessages,
      );
      if (state.resolved) return state.terminalStatus;
      // Only a turn that moved on since the last attempt resets the backoff:
      // a /stream that closes at once must not reconnect every second.
      failures = state.lastEventId === lastEventId ? failures + 1 : 0;
      lastEventId = state.lastEventId;

      // Small delay so React paints restored content before SSE resumes.
      await pause(PAINT_DELAY_MS, signal);
      if (signal.aborted) break;

      logger.info(
        `[SSEReconnect] lastEventId=${state.lastEventId} ` +
          `for message ${messageId}`,
      );
      await reconnectWithState(
        projectId,
        threadId,
        messageId,
        getAccessToken(),
        controller,
        streamProcessor,
        state.lastEventId,
      );
    } catch (error) {
      if (signal.aborted) break;
      failures += 1;
      logger.warn(
        `[SSEReconnect] attempt ${attempt} failed for ${messageId}`,
        error,
      );
    }
  }
  return 'aborted';
}

/**
 * Stage 3: REST polling fallback.
 *
 * Polls the messages endpoint every `intervalMs` milliseconds until all
 * assistant messages reach a terminal state, then calls `onDone()`.
 *
 * @returns A cleanup function that cancels the interval immediately.
 */
export function startRestPolling(
  projectId: string,
  threadId: string,
  intervalMs: number,
  setMessages: SetMessages,
  onDone: () => void,
): () => void {
  let cancelled = false;
  let attempts = 0;

  const intervalId = setInterval(async () => {
    if (cancelled) return;

    attempts += 1;

    // Hard deadline: if the message is stuck in STREAMING in Postgres with
    // no Valkey state (e.g. consume_stream_to_valkey crashed before writing
    // any state), fail_if_stuck is only triggered by GET /state, not by the
    // messages endpoint.  After POLL_MAX_ATTEMPTS we force-close polling and
    // mark any still-streaming message as 'error' so the UI unblocks.
    if (attempts > POLL_MAX_ATTEMPTS) {
      logger.warn(
        `[RestPolling] exceeded ${POLL_MAX_ATTEMPTS} attempts for ` +
          `thread ${threadId}, forcing terminal state`,
      );
      setMessages((prev) =>
        prev.map((msg) =>
          msg.role === 'assistant' && msg.status === 'streaming'
            ? { ...msg, status: 'error' as const }
            : msg,
        ),
      );
      clearInterval(intervalId);
      onDone();
      return;
    }

    try {
      const apiMessages = await getThreadMessages(projectId, threadId);
      const updated = transformApiMessagesToThreadMessages(
        apiMessages,
        threadId,
      );

      // Only assistant rows drive "done" for this recovery path. Require
      // assistantMsgs.length > 0 before stopping: an empty filter makes
      // [].every(...) vacuously true in JS, which would incorrectly clear
      // polling if the API returned no assistant messages yet.
      const assistantMsgs = updated.filter(
        (msg) => msg.role === 'assistant',
      );
      const stillStreaming = assistantMsgs.some(
        (msg) => msg.status === 'streaming',
      );
      const allTerminal =
        assistantMsgs.length > 0 &&
        assistantMsgs.every((msg) => isTerminalStatus(msg.status));

      // Stop when we have at least one assistant row and either every
      // assistant is terminal, or none are still streaming (e.g. pending).
      if (
        assistantMsgs.length > 0 &&
        (allTerminal || !stillStreaming)
      ) {
        setMessages((prev) => [
          ...prev.filter((m) => m.threadId !== threadId),
          ...updated,
        ]);
        clearInterval(intervalId);
        onDone();
      }
    } catch (error) {
      logger.error('[RestPolling] error polling thread messages', error);
    }
  }, intervalMs);

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
}
